<template>
  <v-tabs color="primary">
    <v-tab>General</v-tab>
    <v-tab>Info</v-tab>
    <v-tab>Campaigns</v-tab>
    <v-tab>Accounting</v-tab>
    <v-tab @change="getHistory">History</v-tab>
    <v-tab-item :key="`affiliate-${item.data.id}-general`">
      <v-progress-linear v-show="preloaderChart"
                         indeterminate
      ></v-progress-linear>
      <v-container fluid>
        <v-row align="center">
          <v-col cols="12" sm="6" class="py-0">
            <v-row align="center" class="mt-0">
              <v-col cols="12" md="6">
                <v-text-field label="ID" hide-details readonly disabled :value="item.data.id"></v-text-field>
              </v-col>
            </v-row>
            <v-row align="center" class="mt-0">
              <v-col cols="12" md="6">
                <v-text-field label="Name" hide-details v-model="name"></v-text-field>
              </v-col>
            </v-row>
            <v-row align="center" class="mt-0">
              <v-col cols="12" md="6">
                <v-select hide-details label="Billing Cycle"
                          :items="[{value:'monthly', text:'monthly'}, {value:'weekly', text:'weekly'}, {value:'semimonthly', text:'semimonthly'}]"
                          v-model="billingCycle"
                ></v-select>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" sm="6" class="py-0">
            <v-row align="center" class="mt-0">
              <v-col cols="12" md="6">
                <v-select hide-details label="AM"
                          :items="managers"
                          v-model="managerId"
                ></v-select>
              </v-col>
            </v-row>
            <v-row align="center" class="mt-0">
              <v-col cols="12" md="6">
                <v-text-field label="Currency" hide-details disabled v-model="currency"></v-text-field>
              </v-col>
            </v-row>
            <v-row align="center" class="mt-0">
              <v-col cols="12" md="6">
                <v-select hide-details label="Status"
                          :items="statuses"
                          v-model="status"
                ></v-select>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" sm="6" class="py-0">
            <v-row align="center" class="mt-0">
              <v-col cols="12" md="6">
                <v-text-field label="Postback" hide-details v-model="postbackUrl"></v-text-field>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-btn
          v-if="!checkInputsNotChanged()"
          fixed
          top
          right
          style="top: 50px"
          color="primary"
          :loading="updating"
          :disabled="updating"
          class="upd-btn ma-0"
          @click="onUpdate"
        >
          Update
        </v-btn>
        <v-row>
          <v-col cols="12" md="3" class="pb-0">
            <v-select hide-details label="Trends" v-model="chartOptions.chartPeriod" :items="[{value: 7, text: 'Last 7 Days'}]"></v-select>
          </v-col>
          <v-col cols="12" class="py-0">
            <v-skeleton-loader :loading="chartLoading" class="mx-auto mt-3" width="100%" height="250px" type="image">
              <apexchart type="line" height="250px" :options="chartOptions" :series="series"></apexchart>
            </v-skeleton-loader>
          </v-col>
        </v-row>
      </v-container>
    </v-tab-item>
    <v-tab-item :key="`affiliate-${item.data.id}-info`">
      <v-progress-linear v-show="preloaderInfo"
                         indeterminate
      ></v-progress-linear>
      <v-container fluid>
        <v-simple-table class="card-simple-table">
          <tbody>
            <tr>
              <td>First Name:</td>
              <td><strong>{{affiliateInfo.firstName}}</strong></td>
            </tr>
            <tr>
              <td>Last Name:</td>
              <td><strong>{{affiliateInfo.lastName}}</strong></td>
            </tr>
            <tr>
              <td>Promoted Verticals:</td>
              <td>
                <strong v-if="affiliateInfo.verticals">{{affiliateInfo.verticals.join(', ')}}</strong>
              </td>
            </tr>
            <tr>
              <td>Promoted Traffic:</td>
              <td><strong v-if="affiliateInfo.trafficTypes">{{affiliateInfo.trafficTypes.join(', ')}}</strong></td>
            </tr>
            <tr>
              <td>Expected Monthly Revenue:</td>
              <td><strong>{{affiliateInfo.expectedMonthlyRevenue}}</strong></td>
            </tr>
            <tr>
              <td>Top Countries:</td>
              <td><strong>{{affiliateInfo.topCountries}}</strong></td>
            </tr>
            <tr>
              <td>Websites:</td>
              <td><strong v-if="affiliateInfo.websites">{{affiliateInfo.websites.join(', ')}}</strong></td>
            </tr>
            <tr>
              <td>Other networks:</td>
              <td><strong v-if="affiliateInfo.otherNetworks">{{affiliateInfo.otherNetworks.join(', ')}}</strong></td>
            </tr>
            <tr>
              <td>Top offers, brands:</td>
              <td><strong>{{affiliateInfo.topPromotedBrands}}</strong></td>
            </tr>
            <tr>
              <td>Learn Source:</td>
              <td><strong>{{affiliateInfo.learnSource}}</strong></td>
            </tr>
            <tr>
              <td>Payment Method:</td>
              <td><strong>{{affiliateInfo.paymentMethod}}</strong></td>
            </tr>
            <template v-if="affiliateInfo.paymentDetails">
              <tr v-for="(paymetItem, index) in affiliateInfo.paymentDetails"
                  :key="`affiliate-${item.data.id}-info-pd-${index}`">
                <td style="text-transform: capitalize">{{paymetItem.field.replaceAll('_', ' ')}}:</td>
                <td><strong>{{paymetItem.value}}</strong></td>
              </tr>
            </template>
          </tbody>
        </v-simple-table>
      </v-container>
    </v-tab-item>
    <v-tab-item :key="`affiliate-${item.data.id}-offers`">
      <v-progress-linear v-show="preloaderAffiliate"
                         indeterminate
      ></v-progress-linear>
      <v-container fluid class="pa-0">
        <v-skeleton-loader :loading="affiliateLoading" class="mb-2 mx-auto" max-width="100%" max-height="320px"
                           type="table-thead, table-tbody">
          <v-data-table
              :headers="cardAffiliate.headers"
              :header-props="{ sortIcon: null, headerClass: 'white-space-no-wrap' }"
              :items="cardAffiliate.items"
              :footer-props="{itemsPerPageOptions: cardAffiliate.pageOptions}"
              :page="1"
              :items-per-page="10"
              loading-text="Loading... Please wait"
              fixed-header
              :class="`elevation-0 mb-0 co-data-table items-size-45`"
              item-class="td_title"
              color="#7F98A5"
          >
            <template v-for="item of cardAffiliate.headers" :slot="`header.${[item.value]}`" slot-scope="{header}">
              <span class="header_title">{{header.text}}</span>
            </template>
            <template v-for="header of cardAffiliate.headers" :slot="`item.${[header.value]}`" slot-scope="{item}">
              <template v-if="header.value === 'currencyId'">
                <v-tooltip top open-delay="400">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">{{item.currencySymbol}}</span>
                  </template>
                  <span>{{item.currencyName}}</span>
                </v-tooltip>
              </template>
              <template v-else>{{item[header.value]}}</template>
            </template>
<!--              body-->
            <template v-for="header of cardAffiliate.headers" :slot="`item.${[header.value]}`" slot-scope="{item}">
              <template v-if="header.value === 'currency'">
                <v-tooltip top open-delay="400">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">{{item.currency}}</span>
                  </template>
                  <span>{{item.currencyName}}</span>
                </v-tooltip>
              </template>
              <template v-else>{{item[header.value]}}</template>
            </template>

          </v-data-table>
        </v-skeleton-loader>
      </v-container>
    </v-tab-item>
    <v-tab-item :key="`affiliate-${item.data.id}-accounting`">
      <v-progress-linear v-show="preloaderAccounting"
                         indeterminate
      ></v-progress-linear>
      <v-container fluid class="pa-0">
        <v-skeleton-loader v-if="accountingLoading" class="mb-2 mx-auto"
                           max-width="100%" max-height="320px"
                           type="table-heading@4"
        >
          <v-data-table
              :headers="cardAffiliateAccounting.headers"
              :header-props="{ sortIcon: null, headerClass: 'white-space-no-wrap' }"
              :items="cardAffiliateAccounting.items"
              :footer-props="{itemsPerPageOptions: cardAffiliateAccounting.pageOptions}"
              :page="1"
              :items-per-page="25"
              loading-text="Loading... Please wait"
              fixed-header
              :class="`elevation-0 mb-0 co-data-table items-size-45`"
              item-class="td_title"
              color="#7F98A5"
          >
            <template v-for="item of cardAffiliateAccounting.headers" :slot="`header.${[item.value]}`" slot-scope="{header}">
              <span class="header_title">{{header.text}}</span>
            </template>
          </v-data-table>

        </v-skeleton-loader>
      </v-container>
    </v-tab-item>
    <v-tab-item :key="`affiliate-${item.data.id}-history`">
      <v-progress-linear v-show="preloaderHistory"
                         indeterminate
      ></v-progress-linear>
      <v-container fluid class="pa-0">
          <v-data-table
            :headers="historyTab.headers"
            :header-props="{ sortIcon: null, headerClass: 'white-space-no-wrap' }"
            :items="historyTab.items"
            :footer-props="{itemsPerPageOptions: historyTab.pageOptions}"
            :page="1"
            :items-per-page="25"
            loading-text="Loading... Please wait"
            fixed-header
            :class="`elevation-0 mb-0 co-data-table items-size-45`"
            item-class="td_title"
            color="#7F98A5"
          >
            <template v-slot:item.dateAdded="{ item }" >
              {{ (new Date(item.dateAdded * 1000)).toLocaleDateString("en-US") }}
            </template>
          </v-data-table>

      </v-container>
    </v-tab-item>
  </v-tabs>
</template>

<script>
import {DateTime} from "luxon";
import store from "@/store";
import {mapGetters} from "vuex";
import { createPeriod } from "@/helpers";

export default {
  props: {
    item: {
      type: Object
    }
  },
  async mounted() {
  },
  methods: {
  },
  computed: {
  },
  name: "AffiliateCard",
  data() {
    return {
    }
  }
}
</script>

<style scoped>

</style>
